import { Command } from 'common/Prose/Commands'
import { computed } from 'mobx'
import { NodeType } from 'prosemirror-model'
import { wrapInList } from 'prosemirror-schema-list'
import { liftTarget } from 'prosemirror-transform'
import { useProseStateContext } from '../ProseStateContext'

/**
 * This [Command](https://prosemirror.net/docs/ref/#state.Command) flattens the
 * nodes in the current selection by lifting them to the best depth for the
 * current selection. We use this to flatten nodes before switching them to a
 * different type of list.
 */
const flattenNodes = (): Command => (state, dispatch) => {
  const tr = state.tr

  tr.selection.ranges.forEach((range) => {
    state.doc.nodesBetween(range.$from.pos, range.$to.pos, (node, pos) => {
      if (node.type.isText) {
        return
      }

      const $fromPos = tr.doc.resolve(tr.mapping.map(pos))
      const $toPos = tr.doc.resolve(tr.mapping.map(pos + node.nodeSize))
      const nodeRange = $fromPos.blockRange($toPos)

      if (!nodeRange) {
        return
      }

      const targetLiftDepth = liftTarget(nodeRange)

      if (node.type.isTextblock && dispatch) {
        const { defaultType } = $fromPos.parent.contentMatchAt($fromPos.index())

        tr.setNodeMarkup(nodeRange.start, defaultType)
      }

      if ((targetLiftDepth || targetLiftDepth === 0) && dispatch) {
        tr.lift(nodeRange, targetLiftDepth)
      }
    })
  })

  dispatch?.(tr)

  return true
}

export const useListToggle = (nodeType: NodeType) => {
  const state = useProseStateContext()

  if (!state?.view) {
    return {
      isActive: false,
      isDisabled: true,
      onPress: () => {},
    }
  }

  const view = state.view

  const isActive = computed(() => state.activeNodes.has(nodeType)).get()

  const onPress = () => {
    flattenNodes()(view.state, view.dispatch)
    if (!isActive) {
      wrapInList(nodeType)(view.state, view.dispatch)
    }
    view.focus()
  }

  return {
    isActive,
    isDisabled: false,
    onPress,
  }
}
